import { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';

interface ContactInfo {
  contact_address?: string;
  contact_email?: string;
  contact_whatsapp?: string;
  contact_facebook?: string;
}

export default function ContactFooter() {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContactInfo();
  }, []);

  const loadContactInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('master_admins')
        .select('contact_address, contact_email, contact_whatsapp, contact_facebook')
        .limit(1)
        .single();

      if (error) {
        console.error('Error cargando información de contacto:', error);
      } else {
        setContactInfo(data || {});
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    if (contactInfo.contact_whatsapp) {
      let whatsappUrl = '';
      
      // Si ya es un enlace completo, usarlo directamente
      if (contactInfo.contact_whatsapp.includes('wa.me') || contactInfo.contact_whatsapp.includes('whatsapp.com')) {
        whatsappUrl = contactInfo.contact_whatsapp;
      } else {
        // Si es solo un número, crear el enlace
        const cleanNumber = contactInfo.contact_whatsapp.replace(/[^0-9]/g, '');
        const message = encodeURIComponent('Hola, me gustaría obtener más información sobre Huellitas Unidas.');
        whatsappUrl = `https://wa.me/${cleanNumber}?text=${message}`;
      }
      
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleFacebookClick = () => {
    if (contactInfo.contact_facebook) {
      let facebookUrl = contactInfo.contact_facebook;
      
      // Si no incluye http/https, agregarlo
      if (!facebookUrl.startsWith('http')) {
        facebookUrl = `https://${facebookUrl}`;
      }
      
      window.open(facebookUrl, '_blank');
    }
  };

  const handleEmailClick = () => {
    if (contactInfo.contact_email) {
      const subject = encodeURIComponent('Consulta sobre Huellitas Unidas');
      const body = encodeURIComponent('Hola,\n\nMe gustaría obtener más información sobre Huellitas Unidas.\n\nGracias.');
      window.open(`mailto:${contactInfo.contact_email}?subject=${subject}&body=${body}`, '_self');
    }
  };

  // No mostrar nada si está cargando o no hay información de contacto
  if (loading || (!contactInfo.contact_address && !contactInfo.contact_email && !contactInfo.contact_whatsapp && !contactInfo.contact_facebook)) {
    return null;
  }

  return (
    <div className="bg-gray-800 text-white py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold mb-2">Contacto</h3>
            <p className="text-gray-400 text-sm">¿Tienes preguntas? ¡Estamos aquí para ayudarte!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Dirección */}
            {contactInfo.contact_address && (
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-fill text-xl text-white"></i>
                </div>
                <h4 className="font-medium mb-2">Dirección</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {contactInfo.contact_address}
                </p>
              </div>
            )}

            {/* Email */}
            {contactInfo.contact_email && (
              <div className="text-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-fill text-xl text-white"></i>
                </div>
                <h4 className="font-medium mb-2">Correo</h4>
                <button
                  onClick={handleEmailClick}
                  className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer break-all"
                >
                  {contactInfo.contact_email}
                </button>
              </div>
            )}

            {/* WhatsApp */}
            {contactInfo.contact_whatsapp && (
              <div className="text-center">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-whatsapp-fill text-xl text-white"></i>
                </div>
                <h4 className="font-medium mb-2">WhatsApp</h4>
                <button
                  onClick={handleWhatsAppClick}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer flex items-center mx-auto"
                >
                  <i className="ri-whatsapp-fill mr-2"></i>
                  Escribir
                </button>
              </div>
            )}

            {/* Facebook */}
            {contactInfo.contact_facebook && (
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-facebook-fill text-xl text-white"></i>
                </div>
                <h4 className="font-medium mb-2">Facebook</h4>
                <button
                  onClick={handleFacebookClick}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer flex items-center mx-auto"
                >
                  <i className="ri-facebook-fill mr-2"></i>
                  Seguir
                </button>
              </div>
            )}
          </div>

          {/* Separador */}
          <div className="border-t border-gray-700 mt-8 pt-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <img
                  src="/images/image.png"
                  alt="Huellitas Unidas Logo"
                  className="w-8 h-8 object-contain"
                />
                <span className="text-lg font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
                  Huellitas Unidas
                </span>
              </div>
              <div className="text-sm text-gray-400">
                <a 
                  href="https://readdy.ai/?origin=logo" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-white transition-colors"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}